import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { UploadSimple, Bank, UsersThreeIcon } from "@phosphor-icons/react";

export default function Importaciones() {
  const [file, setFile] = useState(null);
  const [remesaFile, setRemesaFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [imports, setImports] = useState([]);
  const [error, setError] = useState("");
  const [importingRemesa, setImportingRemesa] = useState(false);

  async function loadImports() {
    try {
      const res = await api.get("/importaciones");

      setImports(res.data);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    loadImports();
  }, []);

  async function handleUpload(e) {
    e.preventDefault();

    if (!file) return;

    setLoading(true);

    setError("");

    setResult(null);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const res = await api.post("/importaciones/censo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResult(res.data);

      setFile(null);

      await loadImports();
    } catch (err) {
      console.error(err);

      setError(err?.response?.data?.detail || "Error importando CSV");
    } finally {
      setLoading(false);
    }
  }

  async function handleUploadRemesa(e) {
    e.preventDefault();

    if (!remesaFile) return;

    setImportingRemesa(true);

    setError("");

    try {
      const formData = new FormData();

      formData.append("file", remesaFile);

      const res = await api.post("/importaciones-remesas/remesa", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResult(res.data);

      setRemesaFile(null);
    } catch (err) {
      console.error(err);

      setError(err?.response?.data?.detail || "Error importando remesa");
    } finally {
      setImportingRemesa(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold mb-6">Importaciones</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <UsersThreeIcon
              size={24}
              weight="duotone"
              className="text-orange-500"
            />
            <h2 className="text-lg font-semibold">Importar censo</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Importa falleros desde un CSV.
          </p>

          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files[0])}
            className="mb-4"
          />

          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <UploadSimple size={18} weight="bold" />
            {loading ? "Importando..." : "Importar CSV"}
          </button>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <Bank size={24} weight="duotone" className="text-orange-500" />

            <h2 className="text-lg font-semibold">Importar remesa</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Importa un Excel bancario para crear automáticamente una remesa y
            sus recibos.
          </p>

          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setRemesaFile(e.target.files[0])}
            className="mb-4"
          />

          <button
            onClick={handleUploadRemesa}
            disabled={!remesaFile || importingRemesa}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <UploadSimple size={18} weight="bold" />

            {importingRemesa ? "Importando..." : "Importar Excel"}
          </button>
        </div>

        {result && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <p>✅ Creados: {result.created}</p>

            <p>🔄 Actualizados: {result.updated}</p>

            <p>⚠️ Omitidos: {result.skipped}</p>

            {result.errors?.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Errores</h3>

                <ul className="text-sm text-red-600 space-y-1">
                  {result.errors.map((e, idx) => (
                    <li key={idx}>
                      Fila {e.row}: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Historial */}
      <div className="bg-white rounded-2xl p-6 shadow border">
        <h2 className="text-xl font-semibold mb-4">Historial</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Fecha</th>

                <th className="py-2">Archivo</th>

                <th className="py-2">Usuario</th>

                <th className="py-2">Creados</th>

                <th className="py-2">Actualizados</th>

                <th className="py-2">Omitidos</th>

                <th className="py-2">Errores</th>
              </tr>
            </thead>

            <tbody>
              {imports.map((imp) => (
                <tr key={imp.id} className="border-b">
                  <td className="py-3">
                    {new Date(imp.created_at).toLocaleString()}
                  </td>

                  <td className="py-3">{imp.filename}</td>

                  <td className="py-3">{imp.created_by?.email}</td>

                  <td className="py-3 text-green-600 font-medium">
                    {imp.created}
                  </td>

                  <td className="py-3 text-blue-600 font-medium">
                    {imp.updated}
                  </td>

                  <td className="py-3 text-yellow-600 font-medium">
                    {imp.skipped}
                  </td>

                  <td className="py-3">
                    {imp.errors?.length > 0 ? (
                      <details>
                        <summary className="cursor-pointer text-red-600">
                          Ver ({imp.errors.length})
                        </summary>

                        <div className="mt-2 space-y-1">
                          {imp.errors.map((err, idx) => (
                            <div
                              key={idx}
                              className="text-xs bg-red-50 border border-red-200 rounded p-2"
                            >
                              <strong>Fila {err.row}</strong>
                              {" — "}
                              {err.reason}
                            </div>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <span className="text-green-600">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {imports.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No hay importaciones aún.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
